'use client';

import type { Content } from '@prismicio/client';
import { useEffect, useState } from 'react';
import { AnimationListItem } from './ListItem/AnimationListItem';

import { useVisible } from '@/hooks';
import { AnimatedListTitle } from './AnimatedListTitle/AnimatedListTitle';
import { VideoList } from './VideoList/VideoList';
import { ProspectData } from '@/types/salesforce';
import NextButton from '@/components/Button/NextButton';
import { MODAL_NAMES } from '@/constants/modals';
import useModal from '@/hooks/useModal';
import styles from './animatedListInner.module.scss';

export const AnimatedListInner = ({
  slice,
  prospect,
}: {
  slice: Content.AnimatedListSlice;
  prospect?: ProspectData;
}) => {
  const [activeItem, setActiveItem] = useState(0);
  const [isVisible, id] = useVisible();
  const { openModal } = useModal();

  const handleOnClick = (itemNumber: number) => setActiveItem(itemNumber);
  const nextVideo = () => {
    const itemsQuantity = slice.primary.list.length;

    if (activeItem < itemsQuantity - 1) {
      setActiveItem((prev) => prev + 1);
    } else {
      setActiveItem(0);
    }
  };

  useEffect(() => {
    if (isVisible) {
      const timing =
        slice.variation === 'engineLanding' && activeItem === 0 ? 12000 : 5000;
      const timeoutId = setTimeout(nextVideo, timing);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeItem, isVisible]);

  return (
    <div className={styles.container}>
      <div className={`${styles.inner} ${styles[slice.variation]}`} id={id}>
        <VideoList
          activeItem={activeItem}
          play={isVisible}
          variation={slice.variation}
        />
        <AnimatedListTitle title={slice.primary.title} isVisible={isVisible} />
        <div className={styles.wrapper}>
          <ul className={styles.list}>
            {slice.primary.list.map((item, index) => (
              <AnimationListItem
                heading={item.heading}
                description={item.description}
                index={index}
                key={index}
                activeItem={activeItem}
                onClick={handleOnClick}
                animationStart={isVisible}
                variation={slice.variation}
              />
            ))}
          </ul>
        </div>
      </div>
      {slice.variation === 'engineLanding' && (
        <div
          onClick={() =>
            openModal({
              name: MODAL_NAMES.engine,
              data: { prospect: prospect || null },
            })
          }
          className={`${styles.engineButtonWrapper} ${isVisible ? styles.isVisibleButton : ''}`}
        >
          <NextButton
            type="button"
            styles={styles.engineButton}
            text="Click to finalize and review your offer"
            isArrow
            buttonType="button"
          />
        </div>
      )}
    </div>
  );
};
